import type { BoundaryKind, JourneyHealthEntry } from '../api/client';

/** Humanize a journey-type enum into a readable label without leaking ids/PII
 * (`product-detail` / `product_detail` → "Product detail"). */
export function journeyTypeLabel(journeyType: string): string {
  const spaced = journeyType.replace(/[-_]/g, ' ').trim();
  if (!spaced) return journeyType;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Boundary phrase for a configuration-required row (FR-8.5). Payment and account
 * boundaries read specifically; everything else stays generic. */
export function boundaryPhrase(boundaryKind: BoundaryKind | null): string {
  if (boundaryKind === 'financial') return 'the payment boundary';
  if (boundaryKind === 'state-changing') return 'an account boundary';
  return 'a transaction boundary';
}

/** The extent a journey can be safely tested to (#42, FR-8.5); `null` means there
 * is no transaction boundary, so testing reaches end-to-end. */
function boundaryExtent(boundaryKind: BoundaryKind | null): string | null {
  if (boundaryKind === 'financial') return 'the payment boundary';
  if (boundaryKind === 'state-changing') return 'the account boundary';
  if (boundaryKind === 'reversible' || boundaryKind === 'uncertain') return 'a transaction boundary';
  return null;
}

/**
 * Honest, tense-aware statement of the tested boundary per journey (#42, FR-8.5).
 * Outcome-neutral by design — the Status column already shows pass/fail, so this
 * states only HOW FAR testing reaches. A journey with a completed run reads
 * "Tested up to …"; a never-run one reads "Will test up to …" (never claiming a
 * test already happened).
 */
export function testedBoundaryPhrase(
  entry: Pick<JourneyHealthEntry, 'boundaryKind' | 'executionStatus'>,
): string {
  const extent = boundaryExtent(entry.boundaryKind);
  const verb = entry.executionStatus !== null ? 'Tested' : 'Will test';
  return extent ? `${verb} up to ${extent}.` : `${verb} end-to-end.`;
}

/** A row is HIGH IMPACT when its latest run failed AND the journey is business-
 * critical (#42). Purely descriptive — it highlights where to look first, and is
 * NOT a release verdict. */
export function isHighImpact(
  entry: Pick<JourneyHealthEntry, 'executionStatus' | 'criticality'>,
): boolean {
  const failed = entry.executionStatus === 'failed' || entry.executionStatus === 'error';
  return failed && entry.criticality === 'high';
}
